import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withRouter } from 'react-router-dom';
import QRCode from 'qrcode.react';
import useClipboard from 'react-use-clipboard';

import Spinner from '../../../../../../components/global/Spinner';
import Button from '../../../../../../components/global/Button';
import { getAsset } from '../../../../../../util/assets';
import Angle from '../../../../../../components/global/Angle';
import LinkIcon from '../../../../../../components/icons/LinkIcon';
import QRIcon from '../../../../../../components/icons/QRIcon';
import MarketingMaterials from '../MarketingMaterials/MarketingMaterials';
import MarketingMaterialSampleModal from '../MarketingMaterialSample';
import getConfig from '../../../../../../util/config';
import { ALL_PHOTOS_MODE } from '../../../../../../constants/album-folder';
import useProShare from './hooks/useProShare';
import InvitationForm from './components/InvitationForm';
import { getUtmParamsForQrCodeUrl } from '../../../../views/AlbumFolder/AlbumFolderMarketing/components/AccountMarketingMaterialPrintTemplate/helpers';
import DismissButton from '../../../../../../components/global/DismissButton';

const propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      folderId: PropTypes.string,
      albumId: PropTypes.string,
    }),
  }),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  albumId: PropTypes.string,
  albumFolderId: PropTypes.string,
  onRequestClose: PropTypes.func,
};

const STEPS = {
  SHARE: 'SHARE',
  MARKETING: 'MARKETING',
  QR: 'QR',
  ALL_PHOTOS_QR: 'ALL_PHOTOS_QR',
};

const ProShare = ({
  match,
  title: titleProp,
  albumId,
  albumFolderId,
  onRequestClose,
}) => {
  const {
    entity,
    entityIsAlbum,
    isOwner,
    onQRCodeDownload,
    onSendOnboardingInvitations,
    onSendAllPhotosAccessInvitations,
    loading,
    submitting,
  } = useProShare({
    folderId: albumFolderId ?? match.params.folderId,
    albumId: albumId ?? match.params.albumId,
  });

  const [step, setStep] = useState(STEPS.MARKETING);
  const [success, setSuccess] = useState(false);

  const inviteUrl = entityIsAlbum
    ? `${getConfig().app.baseUrl}/galleries/o/album/${entity?.id}`
    : `${getConfig().app.baseUrl}/galleries/o/album-folder/${entity?.id}`;
  const allPhotosUrl = entity?.web_app_urls?.all_photos_access_url;

  const [copied, setCopied] = useClipboard(inviteUrl);
  const [allPhotosCopied, setAllPhotosCopied] = useClipboard(allPhotosUrl);

  if (!entity || loading) {
    return (
      <div className="d-flex">
        <div className="p-5 m-auto">
          <Spinner />
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="px-2 pb-3 d-flex flex-column align-items-center">
        <img
          src={getAsset('/waldo-excited.png')}
          alt="Waldo logo"
          className="mb-2"
          width="60"
        />
        <h3>Your invites have been sent!</h3>
      </div>
    );
  }

  const publicSharingEnabled = entity?.joins_enabled || entity?.is_web_gallery;

  const privateSharingEnabled =
    (entity.all_photos_mode === ALL_PHOTOS_MODE.PUBLIC &&
      !entity?.joins_enabled) ||
    entity.all_photos_mode === ALL_PHOTOS_MODE.PRIVATE;

  const title = titleProp ?? (
    <Fragment>
      Invite to <span className="text-primary">{entity?.name}</span>
    </Fragment>
  );

  return (
    <Fragment>
      <div className="px-lg-2 pb-3">
        {![STEPS.QR, STEPS.ALL_PHOTOS_QR].includes(step) && isOwner && (
          <Fragment>
            <div className="d-flex mb-0 align-items-center mb-4">
              <h3 className="text-center w-100 mb-0">{title}</h3>
              {onRequestClose && (
                <DismissButton
                  className="contributor-dismiss-btn"
                  onClick={onRequestClose}
                />
              )}
            </div>
            <div className="shutterbug-share-section-selector">
              <Button
                btnStyle="reset"
                className={classNames({
                  'shutterbug-share-section-button': true,
                  'is-active': step === STEPS.SHARE,
                })}
                onClick={() => setStep(STEPS.SHARE)}
              >
                Shareable links
              </Button>
              <Button
                btnStyle="reset"
                className={classNames({
                  'shutterbug-share-section-button': true,
                  'is-active': step === STEPS.MARKETING,
                })}
                onClick={() => setStep(STEPS.MARKETING)}
              >
                Print or post
              </Button>
            </div>
          </Fragment>
        )}

        {step === STEPS.SHARE && (
          <Fragment>
            {publicSharingEnabled && (
              <Fragment>
                <h4 className="mb-0 mt-2">Public link</h4>
                <p className="text-sm mb-1">
                  Anyone with the link can join your gallery
                </p>
                <div className="d-flex">
                  <Button
                    onClick={setCopied}
                    btnStyle="reset"
                    className="shutterbug-share-action-button mr-1"
                  >
                    <div>
                      <LinkIcon color="#0d84fe" size={20} />
                    </div>
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                  <Button
                    onClick={() => setStep(STEPS.QR)}
                    btnStyle="reset"
                    className="shutterbug-share-action-button"
                  >
                    <div>
                      <QRIcon color="#0d84fe" size={20} />
                    </div>
                    QR
                  </Button>
                </div>
                <InvitationForm
                  title="Invite via email or phone"
                  sendInvites={onSendOnboardingInvitations}
                  onSuccess={() => setSuccess(true)}
                  submitting={submitting}
                />
              </Fragment>
            )}
            {publicSharingEnabled && privateSharingEnabled && <hr />}

            {privateSharingEnabled && allPhotosUrl && (
              <Fragment>
                <h4 className="mb-0 mt-2">Watermarked Gallery link</h4>
                <p className="text-sm mb-1">
                  Anyone with the link can access your gallery to view and
                  purchase all the published photos with Faceblocker protection.
                </p>
                <div className="d-flex">
                  <Button
                    onClick={setAllPhotosCopied}
                    btnStyle="reset"
                    className="shutterbug-share-action-button mr-1"
                  >
                    <div>
                      <LinkIcon color="#0d84fe" size={20} />
                    </div>
                    {allPhotosCopied ? 'Copied!' : 'Copy'}
                  </Button>
                  <Button
                    onClick={() => setStep(STEPS.ALL_PHOTOS_QR)}
                    btnStyle="reset"
                    className="shutterbug-share-action-button"
                  >
                    <div>
                      <QRIcon color="#0d84fe" size={20} />
                    </div>
                    QR
                  </Button>
                </div>
                <InvitationForm
                  title="Share ‘ALL photos’ link through email or phone"
                  sendInvites={onSendAllPhotosAccessInvitations}
                  onSuccess={() => setSuccess(true)}
                  submitting={submitting}
                />
              </Fragment>
            )}
          </Fragment>
        )}

        {step === STEPS.MARKETING && (
          <MarketingMaterials
            entity={entity}
            albumFolderId={
              entityIsAlbum ? entity?.root_album_folder?.id : entity?.id
            }
            isOwner
          />
        )}

        {[STEPS.QR, STEPS.ALL_PHOTOS_QR].includes(step) && (
          <Fragment>
            <Button
              onClick={() => setStep(STEPS.SHARE)}
              btnStyle="link"
              className="mb-2 text-bold"
            >
              <Angle direction="left" className="mr-1" /> Back
            </Button>
            <div className="d-flex flex-column align-items-center">
              <h3 className="mb-4">Share QR Code</h3>
              <QRCode
                id={`${entity.id}-qr`}
                value={getUtmParamsForQrCodeUrl({
                  url: step === STEPS.QR ? inviteUrl : allPhotosUrl,
                  albumFolder: {
                    ...entity,
                    account: entity?.account ?? entity.creator,
                  },
                })}
                renderAs="canvas"
                size={200}
                className="mb-4"
              />
              <Button onClick={onQRCodeDownload}>Download PNG</Button>
            </div>
          </Fragment>
        )}
      </div>

      <MarketingMaterialSampleModal />
    </Fragment>
  );
};

ProShare.propTypes = propTypes;

export { ProShare as ProShareComp };

export default withRouter(ProShare);
